/**
 * PhotoCapture — Reusable camera / file-input component for POD photos.
 *
 * On mobile PWA it opens the native camera via `capture="environment"`.
 * Supports multiple photos with preview thumbnails.
 */

import { useRef, useState } from 'react';
import { Camera, X, Plus, ImageIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface PhotoCaptureProps {
  /** Already-captured photos */
  photos: File[];
  /** Called when a new photo is taken/selected */
  onAdd: (file: File) => void;
  /** Called to remove a photo by index */
  onRemove: (index: number) => void;
  /** Maximum number of photos allowed */
  maxPhotos?: number;
  /** Whether field is disabled */
  disabled?: boolean;
}

export default function PhotoCapture({
  photos,
  onAdd,
  onRemove,
  maxPhotos = 5,
  disabled = false,
}: PhotoCaptureProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [previews, setPreviews] = useState<string[]>([]);

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    onAdd(file);
    // Generate preview URL
    setPreviews(prev => [...prev, URL.createObjectURL(file)]);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleRemove = (index: number) => {
    // Revoke preview URL to free memory
    if (previews[index]) URL.revokeObjectURL(previews[index]);
    setPreviews(prev => prev.filter((_, i) => i !== index));
    onRemove(index);
  };

  const canAdd = photos.length < maxPhotos && !disabled;

  return (
    <div className="space-y-2">
      <label className="text-xs font-medium text-muted-foreground flex items-center gap-1">
        <Camera className="h-3.5 w-3.5" />
        Photos ({photos.length}/{maxPhotos})
      </label>

      {/* Thumbnail grid */}
      <div className="flex gap-2 flex-wrap">
        {previews.map((url, idx) => (
          <div key={idx} className="relative w-16 h-16 rounded-md overflow-hidden border">
            <img src={url} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
            {!disabled && (
              <button
                type="button"
                onClick={() => handleRemove(idx)}
                className="absolute top-0 right-0 bg-black/50 text-white rounded-bl p-0.5"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}

        {/* Add button */}
        {canAdd && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            className="w-16 h-16 rounded-md border-2 border-dashed border-muted-foreground/30 flex flex-col items-center justify-center text-muted-foreground hover:border-primary hover:text-primary transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span className="text-[9px]">Add</span>
          </button>
        )}
      </div>

      {/* Hidden file input — opens camera on mobile */}
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        disabled={disabled}
      />
    </div>
  );
}
